import {
  GrantHistoryType,
  GrantsHistoryInterfaceType,
  GrantsHookReturnType
} from '@clientos/types';
import EventNames from '../../../../config/eventNames';
import { PromiseHelper } from '../../../../utils/PromiseHelper';
import { GrantHistoryOptionsType } from '../../../../services/grants/grantsHistory/types';
import { AuthContextEnum } from '../../../../services/authTokenService';
import { getContainers, getServices } from '../../../../infra/startup';
import IEventService from '../../../../services/eventService/IEventService';
import bindAllMethods from '../../../../utils/bindAllMethods';
import { IGrantHistoryService } from '../../../../services/grants/grantsHistory/IGrantHistoryService';
import { Refreshable } from '../../../../utils/refreshable/IRefreshable';
import { IContainer } from '../../../../core/containers';

export class GrantsHistoryInterface extends Refreshable {
  private defaultKey = 'default';
  private initializedAuthContext: Map<
    AuthContextEnum | typeof this.defaultKey,
    boolean
  > = new Map();
  private promiseHelper = new PromiseHelper();

  private eventsService: IEventService;
  private accessControlContainer: IContainer;
  private grantHistoryService: IGrantHistoryService;

  constructor() {
    super();
    const services = getServices();
    const containers = getContainers();
    this.eventsService = services.eventService;
    this.accessControlContainer = containers.accessControlContainer;
    this.grantHistoryService = services.grantHistoryService;
    bindAllMethods(this);

    // new schema
    this.setRefreshMethods({
      init: this._initialize.bind(this),
      refresh: this.refresh.bind(this)
    });

    // old schema
    this.accessControlContainer.addInitServices(
      this._initialize,
      undefined,
      'GrantsHistoryInterface'
    );
    this.accessControlContainer.addRefreshServices(
      this.refresh,
      undefined,
      'GrantsHistoryInterface'
    );
  }

  getInterface(): GrantsHistoryInterfaceType {
    return {
      refresh: this.refresh,
      checkGrantsHistory: this.checkGrantsHistory,
      useReactCheckGrantsHistory: this.useReactCheckGrantsHistory
    };
  }

  private async _initialize(options?: GrantHistoryOptionsType) {
    const contextKey = options?.authContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(contextKey);
    const isInitialized = this.initializedAuthContext.get(contextKey);

    if (!isInitialized) {
      this.initializedAuthContext.set(contextKey, true);
      if (!promise) {
        promise = this.refresh(options);
        this.promiseHelper.setPendingPromise(contextKey, promise);
      }
    }

    await promise;
  }

  private async refreshAction(options?: GrantHistoryOptionsType) {
    if (await this.grantHistoryService.haveGrantsHistoryChanged(options)) {
      this.eventsService.publish(
        EventNames.shellGrantsHistoryChangedEventName,
        undefined
      );
    }
  }

  async refresh(options?: GrantHistoryOptionsType): Promise<void> {
    const promiseKey = options?.authContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(promiseKey);
    if (this.grantHistoryService.isEnabled() && !promise) {
      promise = this.refreshAction(options);
      this.promiseHelper.setPendingPromise(promiseKey, promise);
    }
    await promise;
  }

  async checkGrantsHistory(
    grants: GrantHistoryType[],
    options?: GrantHistoryOptionsType
  ): Promise<boolean> {
    await this._initialize(options);
    return this.grantHistoryService.checkGrantsHistory(grants, options);
  }

  private useReactCheckGrantsHistory(
    _React: any,
    grants: GrantHistoryType[],
    options?: GrantHistoryOptionsType
  ): GrantsHookReturnType {
    const [result, setResult] = _React.useState({
      data: undefined,
      error: undefined,
      loading: true
    });

    const isMountedRef = _React.useRef(true);
    const optionsRef = _React.useRef(options);
    const handleDataCountRef = _React.useRef(0);

    const handleData = _React.useCallback(async () => {
      const thisCount = handleDataCountRef.current + 1;
      handleDataCountRef.current = thisCount;

      const newData = await this.checkGrantsHistory(grants, optionsRef.current)
        .then((data) => ({ data, error: undefined, loading: false }))
        .catch((error) => ({ data: undefined, error, loading: false }));

      if (!isMountedRef.current) {
        console.warn(`Component unmounted before receiving data`);
      } else if (thisCount !== handleDataCountRef.current) {
        console.warn(`Race condition detected, discarding stale data`);
      } else {
        setResult(newData);
      }
    }, []);

    _React.useEffect(() => {
      handleData();
    }, [
      handleData,
      JSON.stringify(grants),
      options?.authContext,
      options?.offset
    ]);

    _React.useEffect(() => {
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    return _React.useMemo(
      () => ({
        ...result,
        reload: () => handleData()
      }),
      [result, handleData]
    );
  }
}
